/**
 * DiseaseTargetsDashboard — sortable target table for disease lncRNA discovery results.
 *
 * Renders when visualization.type === "disease_dashboard".
 * Each row has inline GRN, Knockdown (pre-fills chat), and Literature (inline panel) actions.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { NetworkGraphViz } from './NetworkGraphViz';
import InfoTooltip from './InfoTooltip';
import { API_BASE_URL } from '../../../services/chatApi';
import { getFirebaseToken } from '../../../auth/CognitoAuth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DashboardTarget {
  symbol: string;
  expression_rank: number;
  priority_rank: number;
  ranking_category: 'CONSENSUS' | 'TOP_EXPRESSION' | 'TOP_NETWORK' | 'DIVERGENT' | string;
  rank_difference: number;
  log2fc: number;
  adj_pvalue: number;
  priority_score: number;
  priority_level: string;
  has_grn: boolean;
  regulation: string;
  top_cell_type?: string;
}

interface DashboardStatistics {
  n_total: number;
  n_consensus: number;
  n_top_expression: number;
  n_top_network: number;
  n_divergent: number;
  n_moderate?: number;
  n_disease_cells?: number;
  n_normal_cells?: number;
  response_time_ms?: number;
}

interface DiseaseTargetsDashboardData {
  type: 'disease_dashboard';
  disease: string;
  dataset_id: string;
  targets: DashboardTarget[];
  statistics: DashboardStatistics;
  cell_types: string[];
}

interface DiseaseTargetsDashboardProps {
  data: DiseaseTargetsDashboardData;
  onAction?: (action: string, params?: any) => void;
}

type SortKey = 'priority_rank' | 'expression_rank' | 'log2fc' | 'priority_score';
type SortDir = 'asc' | 'desc';

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const RegBadge: React.FC<{ regulation: string }> = ({ regulation }) => {
  const up = regulation?.toLowerCase().includes('up');
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-semibold ${up ? 'bg-red-900/30 text-red-400' : 'bg-blue-900/30 text-blue-400'}`}>
      {up ? '↑' : '↓'}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Literature inline panel
// ---------------------------------------------------------------------------

interface LiteratureData {
  gemini_summary?: string;
  papers?: Array<{ title?: string; year?: number; pmid?: string | number }>;
}

const LiteraturePanel: React.FC<{ data: LiteratureData; symbol: string }> = ({ data, symbol }) => {
  const papers = data.papers ?? [];
  const summary = data.gemini_summary ?? '';

  if (!summary && papers.length === 0) {
    return (
      <div className="p-3 text-sm text-gray-400 italic">
        No literature found for {symbol}.
      </div>
    );
  }

  return (
    <div className="p-3 space-y-3">
      {summary && (
        <div className="p-3 bg-purple-950/30 border border-purple-800 rounded text-xs text-gray-300 leading-relaxed">
          <span className="font-semibold text-purple-400 block mb-1">AI Summary</span>
          {summary}
        </div>
      )}
      {papers.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-semibold text-gray-400">Papers ({papers.length})</p>
          {papers.map((p, i) => (
            <div key={i} className="text-xs text-gray-300 flex items-start gap-2">
              <span className="text-gray-400 shrink-0">{p.year ?? '—'}</span>
              <span className="flex-1">{p.title ?? 'Untitled'}</span>
              {p.pmid && (
                <a
                  href={`https://pubmed.ncbi.nlm.nih.gov/${p.pmid}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-500 hover:underline shrink-0"
                >
                  PMID:{p.pmid}
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Targets Tab
// ---------------------------------------------------------------------------

const TargetsTab: React.FC<{
  targets: DashboardTarget[];
  disease: string;
  datasetId: string;
  onKnockdown: (target: DashboardTarget) => void;
}> = ({ targets, disease, datasetId, onKnockdown }) => {
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('priority_rank');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // GRN state
  const [loadingGRNSymbol, setLoadingGRNSymbol] = useState<string | null>(null);
  const [expandedGRN, setExpandedGRN] = useState<{ symbol: string; data: any } | null>(null);
  const [grnError, setGrnError] = useState<string | null>(null);

  // Literature state
  const [loadingLitSymbol, setLoadingLitSymbol] = useState<string | null>(null);
  const [expandedLit, setExpandedLit] = useState<{ symbol: string; data: LiteratureData } | null>(null);
  const [litError, setLitError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let list = targets;
    if (filter.trim()) {
      const q = filter.trim().toLowerCase();
      list = list.filter(t => t.symbol.toLowerCase().includes(q));
    }
    const sign = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => sign * (a[sortKey] - b[sortKey]));
  }, [targets, filter, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const handleGRN = useCallback(async (target: DashboardTarget) => {
    if (expandedGRN?.symbol === target.symbol) {
      setExpandedGRN(null);
      return;
    }
    setLoadingGRNSymbol(target.symbol);
    setExpandedGRN(null);
    setGrnError(null);
    try {
      const token = await getFirebaseToken();
      const params = new URLSearchParams({ disease, dataset_id: datasetId });
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 180_000);
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/lncrna-profile/${encodeURIComponent(target.symbol)}/grn?${params}`,
        { signal: controller.signal, headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      clearTimeout(timeout);
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.detail?.message ?? `HTTP ${resp.status}`);
      }
      const json = await resp.json();
      const raw = json.grn_data ?? {};
      // NetworkGraphViz expects 'normal_grn'; precomputed GRN stores it as 'control_grn'
      const vizData = {
        ...raw,
        normal_grn: raw.control_grn,
        statistics: {
          total_nodes: raw.disease_grn?.nodes?.length ?? 0,
          total_edges: raw.disease_grn?.edges?.length ?? 0,
          enrichment_score: raw.enrichment_score,
          metrics: raw.metrics,
        },
      };
      setExpandedGRN({ symbol: target.symbol, data: vizData });
    } catch (err: any) {
      setGrnError(
        err?.name === 'AbortError'
          ? `GRN computation timed out for ${target.symbol}.`
          : `Failed to load GRN for ${target.symbol}: ${err.message}`
      );
    } finally {
      setLoadingGRNSymbol(null);
    }
  }, [disease, datasetId, expandedGRN]);

  const handleLiterature = useCallback(async (target: DashboardTarget) => {
    if (expandedLit?.symbol === target.symbol) {
      setExpandedLit(null);
      return;
    }
    setLoadingLitSymbol(target.symbol);
    setExpandedLit(null);
    setLitError(null);
    try {
      const token = await getFirebaseToken();
      const params = new URLSearchParams({ disease });
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/lncrna-profile/${encodeURIComponent(target.symbol)}/literature?${params}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.detail?.message ?? `HTTP ${resp.status}`);
      }
      const json = await resp.json();
      const lit: LiteratureData = json.literature ?? { papers: [], gemini_summary: '' };
      setExpandedLit({ symbol: target.symbol, data: lit });
    } catch (err: any) {
      setLitError(`Failed to load literature for ${target.symbol}: ${err.message}`);
    } finally {
      setLoadingLitSymbol(null);
    }
  }, [expandedLit, disease]);

  const downloadCSV = () => {
    const header = 'Symbol,Priority Rank,Expression Rank,Category,Regulation,Log2FC,Adj P-Value,Priority Score,Has GRN,Top Cell Type\n';
    const rows = filtered.map(t =>
      `${t.symbol},${t.priority_rank},${t.expression_rank},${t.ranking_category},${t.regulation},${t.log2fc.toFixed(3)},${t.adj_pvalue.toExponential(2)},${t.priority_score.toFixed(3)},${t.has_grn},${t.top_cell_type ?? ''}`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${disease.replace(/\s+/g, '_')}_targets.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const SortIcon: React.FC<{ col: SortKey }> = ({ col }) =>
    sortKey === col
      ? <span className="ml-1 text-blue-500">{sortDir === 'asc' ? '↑' : '↓'}</span>
      : <span className="ml-1 text-gray-300">↕</span>;

  const TOTAL_COLS = 8; // Rank | Gene | Reg. | Log2FC | Score | GRN | Knockdown | Literature

  return (
    <div className="space-y-3">
      {/* Controls */}
      <div className="flex flex-wrap gap-2 items-center">
        <input
          type="text"
          placeholder="Filter by gene…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="border border-gray-700 rounded px-2 py-1 text-sm bg-gray-800 text-gray-200 focus:outline-none focus:border-blue-400 w-36"
        />
        <button
          onClick={downloadCSV}
          className="ml-auto text-xs text-blue-400 hover:text-blue-300 border border-blue-700 rounded px-2 py-1"
        >
          ↓ CSV
        </button>
      </div>

      {/* Error banners */}
      {grnError && (
        <div className="text-sm text-orange-400 bg-orange-900/30 border border-orange-700 rounded p-2">
          {grnError}
        </div>
      )}
      {litError && (
        <div className="text-sm text-orange-400 bg-orange-900/30 border border-orange-700 rounded p-2">
          {litError}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded border border-gray-700">
        <table className="w-full text-xs">
          <thead className="bg-gray-800 text-gray-400 uppercase text-[10px] tracking-wide">
            <tr>
              <th className="px-3 py-2 text-left cursor-pointer select-none" onClick={() => toggleSort('priority_rank')}>
                <span className="flex items-center gap-1">
                  Rank <SortIcon col="priority_rank" />
                  <InfoTooltip title="Priority Rank" content="Combined rank from expression differential (log2FC) and GRN network enrichment. Rank #1 = best therapeutic candidate." />
                </span>
              </th>
              <th className="px-3 py-2 text-left">Gene</th>
              <th className="px-3 py-2 text-left">
                <span className="flex items-center gap-1">
                  Reg.
                  <InfoTooltip title="Regulation" content="↑ Upregulated in disease vs normal. ↓ Downregulated. Based on log2FC direction." />
                </span>
              </th>
              <th className="px-3 py-2 text-right cursor-pointer select-none" onClick={() => toggleSort('log2fc')}>
                <span className="flex items-center justify-end gap-1">
                  Log2FC <SortIcon col="log2fc" />
                  <InfoTooltip title="Log2 Fold-Change" content="Log2(disease / normal) expression ratio. |log2FC| ≥ 1 means ≥2x change. Positive = upregulated, negative = downregulated." position="left" />
                </span>
              </th>
              <th className="px-3 py-2 text-right cursor-pointer select-none" onClick={() => toggleSort('priority_score')}>
                <span className="flex items-center justify-end gap-1">
                  Score <SortIcon col="priority_score" />
                  <InfoTooltip title="Priority Score" content="Combined priority score (0–1) weighting expression rank and GRN network enrichment. Higher = stronger therapeutic candidate." position="left" />
                </span>
              </th>
              <th className="px-3 py-2 text-center" colSpan={3}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={TOTAL_COLS} className="px-3 py-4 text-center text-gray-400">No targets match this filter</td>
              </tr>
            ) : (
              filtered.map(t => {
                const isGRNExpanded = expandedGRN?.symbol === t.symbol;
                const isLitExpanded = expandedLit?.symbol === t.symbol;
                const isLoadingGRN = loadingGRNSymbol === t.symbol;
                const isLoadingLit = loadingLitSymbol === t.symbol;
                return (
                  <React.Fragment key={t.symbol}>
                    <tr className="hover:bg-gray-800 transition-colors">
                      <td className="px-3 py-2 font-medium text-gray-300">#{t.priority_rank}</td>
                      <td className="px-3 py-2 font-semibold text-gray-100">{t.symbol}</td>
                      <td className="px-3 py-2"><RegBadge regulation={t.regulation} /></td>
                      <td className="px-3 py-2 text-right font-mono">
                        <span className={t.log2fc > 0 ? 'text-red-400' : 'text-blue-400'}>
                          {t.log2fc > 0 ? '+' : ''}{t.log2fc.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right text-gray-300">{t.priority_score.toFixed(2)}</td>

                      {/* GRN button */}
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => handleGRN(t)}
                          disabled={isLoadingGRN}
                          title="View gene regulatory network"
                          className={`text-xs px-2 py-1 rounded border transition-colors disabled:opacity-50 ${
                            isGRNExpanded
                              ? 'border-blue-600 bg-blue-900/30 text-blue-400'
                              : 'border-blue-700 text-blue-400 hover:bg-blue-900/30'
                          }`}
                        >
                          {isLoadingGRN
                            ? (t.has_grn ? 'Loading…' : 'Computing…')
                            : isGRNExpanded ? 'Hide GRN' : 'GRN'}
                        </button>
                      </td>

                      {/* Knockdown button */}
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => onKnockdown(t)}
                          title="Pre-fill chat with knockdown simulation query"
                          className="text-xs px-2 py-1 rounded border border-purple-700 text-purple-400 hover:bg-purple-900/30 transition-colors"
                        >
                          Knockdown
                        </button>
                      </td>

                      {/* Literature button */}
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => handleLiterature(t)}
                          disabled={isLoadingLit}
                          title="View literature for this gene"
                          className={`text-xs px-2 py-1 rounded border transition-colors disabled:opacity-50 ${
                            isLitExpanded
                              ? 'border-green-600 bg-green-900/30 text-green-400'
                              : 'border-green-700 text-green-400 hover:bg-green-900/30'
                          }`}
                        >
                          {isLoadingLit ? 'Loading…' : isLitExpanded ? 'Hide Lit.' : 'Literature'}
                        </button>
                      </td>
                    </tr>

                    {/* Inline GRN expansion */}
                    {isGRNExpanded && expandedGRN?.data && (
                      <tr>
                        <td colSpan={TOTAL_COLS} className="pb-4 pt-2 px-3">
                          <div className="border border-gray-700 rounded-lg overflow-hidden">
                            <div className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700">
                              <span className="text-xs font-medium text-gray-400">
                                GRN — {t.symbol} / {disease}
                              </span>
                            </div>
                            <NetworkGraphViz data={expandedGRN.data} interactive={false} />
                          </div>
                        </td>
                      </tr>
                    )}

                    {/* Inline Literature expansion */}
                    {isLitExpanded && expandedLit?.data && (
                      <tr>
                        <td colSpan={TOTAL_COLS} className="pb-4 pt-2 px-3">
                          <div className="border border-green-800 rounded-lg overflow-hidden">
                            <div className="px-3 py-2 bg-green-900/30 border-b border-green-800">
                              <span className="text-xs font-medium text-green-400">
                                Literature — {t.symbol}
                              </span>
                            </div>
                            <LiteraturePanel data={expandedLit.data} symbol={t.symbol} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400 text-right">{filtered.length} of {targets.length} targets</p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const DiseaseTargetsDashboard: React.FC<DiseaseTargetsDashboardProps> = ({ data, onAction }) => {
  const { disease, dataset_id, targets, statistics, cell_types } = data;

  const handleKnockdown = useCallback((target: DashboardTarget) => {
    if (onAction) {
      onAction('simulate_knockdown', {
        gene: target.symbol,
        disease,
        dataset_id,
        cell_type: target.top_cell_type || undefined,
      });
    }
  }, [onAction, disease, dataset_id]);

  return (
    <div className="origin-card mt-4 overflow-hidden">
      {/* Header */}
      <div className="px-4 pt-4 pb-2 border-b border-gray-700">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-base font-semibold text-gray-100 capitalize">
            {disease} — Target Dashboard
          </h3>
          <span className="text-xs text-gray-400">
            {statistics.n_total} targets
          </span>
        </div>
        <p className="text-xs text-gray-500">
          Cell types: {(cell_types ?? ['all']).join(', ')}
        </p>
      </div>

      {/* Content */}
      <div className="p-4">
        <TargetsTab
          targets={targets}
          disease={disease}
          datasetId={dataset_id}
          onKnockdown={handleKnockdown}
        />
      </div>
    </div>
  );
};

export default DiseaseTargetsDashboard;
